// frontend/src/components/BulkActionBar.jsx
import React, { useState } from "react";
import PropTypes from "prop-types";

/**
 * Flatten a folder tree into indented options.
 * @param {Array} nodes
 * @param {number} depth
 * @returns {Array<{id, label}>}
 */
function flattenFolders(nodes, depth = 0) {
  const out = [];
  for (const n of nodes) {
    out.push({ id: n.id, label: `${"— ".repeat(depth)}${n.name}` });
    if (n.children && n.children.length) {
      out.push(...flattenFolders(n.children, depth + 1));
    }
  }
  return out;
}

/**
 * BulkActionBar — floating action bar shown when ≥ 2 codes are selected (P14-G8).
 *
 * Activate / Deactivate / Move to folder / Add tag / Delete. "Move" and "Add
 * tag" reveal an inline picker so the user chooses the target before applying.
 *
 * @param {object}   props
 * @param {number}   props.count      Number of selected codes
 * @param {Array}    props.folders    Folder tree (for the move picker)
 * @param {Array}    props.tags       Org tags (for the tag picker)
 * @param {Function} props.onAction   (action, opts) => void  — opts may carry
 *                                     { folderId } or { tagId }
 * @param {Function} props.onClear    () => void — deselect all
 * @param {boolean}  props.busy       Disables buttons while a bulk call runs
 */
function BulkActionBar({ count, folders, tags, onAction, onClear, busy }) {
  // null | "move" | "tag" — which inline picker is open.
  const [picker, setPicker] = useState(null);
  const folderOptions = flattenFolders(folders);

  const BTN =
    "px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 transition-colors";

  return (
    <div className="sticky bottom-4 z-30 flex justify-center">
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 shadow-lg">
        <span className="text-sm font-medium text-gray-700">
          {count} selected
        </span>
        <span className="h-5 w-px bg-gray-200" />

        {picker === "move" ? (
          <div className="flex items-center gap-2">
            <select
              aria-label="Move to folder"
              defaultValue=""
              disabled={busy}
              onChange={(e) =>
                onAction("move", { folderId: e.target.value || null })
              }
              className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
            >
              <option value="" disabled>
                Choose folder…
              </option>
              <option value="">Root (no folder)</option>
              {folderOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
            <button type="button" onClick={() => setPicker(null)} className={BTN}>
              Cancel
            </button>
          </div>
        ) : picker === "tag" ? (
          <div className="flex items-center gap-2">
            <select
              aria-label="Add tag"
              defaultValue=""
              disabled={busy}
              onChange={(e) =>
                e.target.value && onAction("tag", { tagId: e.target.value })
              }
              className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
            >
              <option value="" disabled>
                Choose tag…
              </option>
              {tags.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            <button type="button" onClick={() => setPicker(null)} className={BTN}>
              Cancel
            </button>
          </div>
        ) : (
          <>
            <button
              type="button"
              disabled={busy}
              onClick={() => onAction("activate")}
              className={BTN}
            >
              Activate
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => onAction("deactivate")}
              className={BTN}
            >
              Deactivate
            </button>
            <button
              type="button"
              disabled={busy || folderOptions.length === 0}
              onClick={() => setPicker("move")}
              className={BTN}
              title={
                folderOptions.length === 0 ? "Create a folder first" : undefined
              }
            >
              Move to folder
            </button>
            <button
              type="button"
              disabled={busy || tags.length === 0}
              onClick={() => setPicker("tag")}
              className={BTN}
              title={tags.length === 0 ? "Create a tag first" : undefined}
            >
              Add tag
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => onAction("delete")}
              className="px-3 py-1.5 text-sm rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-40 transition-colors"
            >
              Delete
            </button>
            <span className="h-5 w-px bg-gray-200" />
            <button
              type="button"
              onClick={onClear}
              className="text-sm text-gray-400 hover:text-gray-700"
            >
              Clear
            </button>
          </>
        )}
      </div>
    </div>
  );
}

BulkActionBar.propTypes = {
  count: PropTypes.number.isRequired,
  folders: PropTypes.array,
  tags: PropTypes.array,
  onAction: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  busy: PropTypes.bool,
};
BulkActionBar.defaultProps = { folders: [], tags: [], busy: false };

export default BulkActionBar;
